import { useState } from 'react';
import { formatLifespanCompact, formatInterval, isShortValue } from '../services/rtSectionTree';
import { DEFAULT_RASITUSLUOKKA } from '../services/useRTKayttoikaVastaukset';

const RL_LABELS = { 1: '1 vaikea', 2: '2 normaali', 3: '3 kevyt' };

// Käyttöikä → Tarkastusväli → Huoltoväli, as a mini label/value table —
// the headline number (what applies at the active rasitusluokka) leads,
// styled after the "minitaulukko" mockup (closest to how the RT card
// itself reads, and scales cleanly to a future extra stat row without
// needing a new icon or layout invented for it). Replaced the earlier
// dot-timeline design with this one.
//
// `long` flags a row whose raw value isn't one of the compact shapes a
// label/value table row was built for (a bare number, range, or R/J/
// Saavutettu symbol) — Talotekniikka's huoltoväli/tarkastusväli are
// frequently a full maintenance-instruction sentence instead, which
// can't sit right-aligned next to its label without the row overflowing.
// A long row stacks (label above, full sentence below) instead; a short
// row stays the clean two-column line.
function buildStatRows(option, rasitusluokka, rakennuksenIka, hasVariableRL) {
  const lifespanRaw = option[`lifespanRL${rasitusluokka}`];
  const insp = option.inspectionIntervalYears;
  const maint = option.maintenanceInterval;
  return [
    {
      key: 'kayttoika', label: hasVariableRL ? `Käyttöikä (RL ${rasitusluokka})` : 'Käyttöikä', raw: lifespanRaw,
      value: lifespanRaw ? formatLifespanCompact(lifespanRaw, rakennuksenIka) : null,
      long: !!lifespanRaw && !isShortValue(lifespanRaw), highlight: true,
    },
    {
      key: 'huolto', label: 'Huoltoväli', raw: maint,
      value: maint ? formatInterval(maint) : null, long: !!maint && !isShortValue(maint),
    },
    {
      key: 'tarkastus', label: 'Tarkastusväli', raw: insp,
      value: insp ? formatInterval(insp) : null, long: !!insp && !isShortValue(insp),
    },
  ].filter(row => row.value);
}

// One question, rendered inline inside its (already-open) leaf section —
// no collapse/expand of its own anymore.
//
// Multi-select: a building can genuinely have more than one variant of the
// same rakenneosa present (e.g. some walls in lautaverhous, others in
// tiiliverhous), so picking an option toggles it on/off.
//
// Keyed/selected by option.optionId, never option.label — a handful of
// rakenneosa (1131 Salaojat, 1251 Parvekkeet) repeat the exact same label
// twice, split only by era/code-compliance (constructionPeriod), so
// constructionPeriod is always shown when present, both as useful context
// and so two look-alike options are still visually distinguishable.
//
// Checkbox-gated: the question/pills/stat table only expand once an
// option is checked. With Talotekniikka's many long option lists (some
// questions run 10-15+ variants), always expanding every option's full
// pills+table regardless of selection made the list unreadably long —
// an unchecked option now collapses to a single checkbox+label row, and
// expands in place once checked. Checking defaults the rasitusluokka to
// 2 normaali (the common case — the pills are there to override it, not
// to force a judgment call just to say "yes, this applies"); the pills
// then only ever change which class applies, they no longer deselect —
// unchecking the box is the one way to remove an option (see
// toggleOption/pickRasitusluokka in useRTKayttoikaVastaukset.js).
// Rasitusluokka still belongs to each SELECTED OPTION, not the rakenneosa
// as a whole — two variants of the same rakenneosa (e.g. a sheltered wall
// vs. an exposed one) can genuinely sit in different exposure conditions.
export default function RTQuestionRow({ question, selectedOptions, rakennuksenIka, onToggleOption, onPickRasitusluokka }) {
  const hideHeading = question.vaihtoehdot.length === 1 && question.vaihtoehdot[0].label === question.nimike;
  const hasAnySelection = question.vaihtoehdot.some(o => selectedOptions[o.optionId]);
  // Any question that still shows its own heading (hideHeading false, so
  // either several variants or one variant named differently from the
  // nimike, e.g. "Ylijännitesuojat" → "Venttiilisuojat") now gates its
  // option(s) behind that heading's own checkbox — a single differently-
  // named option used to render unconditionally, no gate at all, showing
  // "Venttiilisuojat" before anyone had said Ylijännitesuojat applies.
  // Collapsed until checked, or until an answer already exists so a
  // previously-answered question isn't hidden behind its own gate.
  const [manuallyExpanded, setManuallyExpanded] = useState(false);
  const optionsOpen = hideHeading || manuallyExpanded || hasAnySelection;

  // Same card padding as a regular option row (below) so the heading's
  // own checkbox lands at the exact same horizontal position as every
  // option's checkbox — they used to sit at different indents (the
  // heading had a bare 2px margin, options sit inside a 9px/11px-padded
  // card), which read as misaligned columns down the list.
  const headingRowStyle = { background: '#fff', borderRadius: 9, padding: '9px 11px', marginBottom: 2 };

  return (
    // borderTop always present, heading or not: with the heading hidden
    // (single option whose label repeats the nimike), there was nothing
    // left to show this is a new, unrelated question rather than one more
    // option tacked onto the previous question's list — e.g. S221
    // "Suurjännitejakelujärjestelmä" reading as a 4th checkbox under the
    // S212 "Sähkön tuotantojärjestelmät..." group above it.
    <div style={{ padding: '2px 14px 14px', borderTop: '1px solid #eef0f2' }}>
      {!hideHeading && (
        <div style={headingRowStyle}>
          <button type="button" onClick={() => setManuallyExpanded(e => !e)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, flex: 1, color: '#1c1c1e' }}>{question.nimike}</span>
            <i className={`bi bi-${optionsOpen ? 'check-square-fill' : 'square'}`} style={{ color: optionsOpen ? '#198754' : '#c7c7cc', fontSize: '0.9rem', flexShrink: 0 }} />
          </button>
        </div>
      )}

      {optionsOpen && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {question.vaihtoehdot.flatMap((option, i) => {
          const sel = selectedOptions[option.optionId];
          const selected = !!sel;
          const rasitusluokka = sel?.rasitusluokka ?? DEFAULT_RASITUSLUOKKA;
          // Sähkö/Tietotekniikka's elinkaari figures aren't split by
          // rasitusluokka at all — every option there has the same value
          // under RL1/RL2/RL3, so offering a vaikea/normaali/kevyt choice
          // that changes nothing would be a fake control.
          const hasVariableRL = option.lifespanRL1 !== option.lifespanRL2 || option.lifespanRL2 !== option.lifespanRL3;
          const rows = buildStatRows(option, rasitusluokka, rakennuksenIka, hasVariableRL);
          const nodes = [];
          // A handful of rakenneosa (e.g. "Maanvastaiset seinät" in 1221/1222,
          // "Alapohjat" in 1223) are a real sub-category of material choices
          // under one construction type, not independent options at the same
          // level as the rakenneosa list itself — flattening them with no
          // visual distinction made them read as just more checkboxes in the
          // same list, with no indication they're actually a group. A small
          // heading (once, when the group changes) plus an indent on the
          // group's own options makes that nesting visible instead of baking
          // it awkwardly into the label text.
          if (option.groupLabel && option.groupLabel !== question.vaihtoehdot[i - 1]?.groupLabel) {
            nodes.push(
              <div key={`group-${i}`} style={{ fontSize: '0.72rem', fontWeight: 700, color: '#495057', margin: '4px 0 0 6px' }}>
                {option.groupLabel}
              </div>
            );
          }
          nodes.push(
            <div key={option.optionId} style={{
              background: '#fff', borderRadius: 9, padding: '9px 11px', display: 'flex', flexDirection: 'column', gap: 8,
              marginLeft: option.groupLabel ? 16 : 0, borderLeft: option.groupLabel ? '2px solid #e5e5ea' : 'none',
            }}>
              <button type="button" onClick={() => onToggleOption(option.optionId)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}>
                {/* The checkbox trails instead of leading when this row IS
                    the heading (its own label stands in for the nimike) —
                    a leading checkbox next to bold text reads like just
                    another option, when a true heading above it (e.g.
                    "Pääkeskuksen syöttöjärjestelmät") has no checkbox at
                    all. Trailing it keeps the bold label the first thing
                    read, matching how a real heading reads. */}
                {!hideHeading && (
                  <i className={`bi bi-${selected ? 'check-square-fill' : 'square'}`} style={{ color: selected ? '#198754' : '#c7c7cc', fontSize: '0.9rem', flexShrink: 0 }} />
                )}
                <span style={{
                  fontSize: hideHeading ? '0.85rem' : '0.82rem', flex: 1, color: selected ? '#198754' : '#1c1c1e',
                  fontWeight: hideHeading ? 600 : (selected ? 600 : 400),
                }}>
                  {option.label}
                  {option.constructionPeriod && <span style={{ color: '#8e8e93', fontWeight: 400 }}> · {option.constructionPeriod}</span>}
                </span>
                {hideHeading && (
                  <i className={`bi bi-${selected ? 'check-square-fill' : 'square'}`} style={{ color: selected ? '#198754' : '#c7c7cc', fontSize: '0.9rem', flexShrink: 0 }} />
                )}
              </button>

              {selected && (
                <>
                  {hasVariableRL && (
                    <div role="group" aria-label="Rasitusluokka" style={{ display: 'flex', background: '#eceef0', borderRadius: 9, padding: 3 }}>
                      {[1, 2, 3].map(rl => {
                        const active = rl === rasitusluokka;
                        return (
                          <button key={rl} type="button" onClick={() => onPickRasitusluokka(option.optionId, rl)}
                            style={{
                              flex: 1, minHeight: 36, border: 'none', borderRadius: 7, fontSize: '0.72rem', fontWeight: active ? 700 : 600,
                              background: active ? '#198754' : 'none', color: active ? '#fff' : '#6c6c70', cursor: 'pointer',
                            }}>
                            {RL_LABELS[rl]}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {rows.length > 0 && (
                    <div style={{ background: '#fafbfa', border: '1px solid #e5e5ea', borderRadius: 9, overflow: 'hidden' }}>
                      {rows.map((row, i) => (
                        <div key={row.key} style={{
                          padding: '9px 12px', borderBottom: i < rows.length - 1 ? '1px solid #eceef0' : 'none',
                          display: row.long ? 'block' : 'grid', gridTemplateColumns: row.long ? undefined : '1fr auto',
                          columnGap: 12, alignItems: 'center',
                        }}>
                          {row.long ? (
                            <>
                              <div style={{ fontSize: '0.68rem', color: '#6c6c70', marginBottom: 4 }}>{row.label}</div>
                              <div style={{ fontSize: '0.72rem', lineHeight: 1.5, fontWeight: 600, color: row.highlight ? '#198754' : '#1c1c1e' }}>{row.raw}</div>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '0.68rem', color: '#6c6c70' }}>{row.label}</span>
                              <span style={{ fontSize: '0.76rem', fontWeight: 700, color: row.highlight ? '#198754' : '#1c1c1e' }}>{row.value}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {option.notes && (
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6c6c70', lineHeight: 1.5 }}>{option.notes}</div>
                  )}
                </>
              )}
            </div>
          );
          return nodes;
        })}
      </div>}
    </div>
  );
}
